import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Shared axios instance — `withCredentials` is kept on so existing auth flows
// (cookie-based session login) continue to work for non-template routes.
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

/**
 * Ecommerce template API helpers.
 *
 * Every helper accepts an optional `fallback` argument: when the backend
 * endpoint isn't wired up yet (404 / network error), we return the fallback
 * instead of throwing — so the templates always have *something* to render.
 * Real customer data takes over the moment the backend is implemented.
 */

const safe = async (run, fallback) => {
  try {
    return await run();
  } catch (e) {
    // Always swallow 404 / network errors when a fallback is provided.
    if (fallback !== undefined) return fallback;
    throw e;
  }
};

// Convert the backend's products schema (`price_cents`, `image_url`,
// product_id-only) into the template's expected shape (`price`, `images[]`,
// `slug`, `category`, etc). Centralised so Shop / ProductDetail / Home all
// see consistent data.
const normalizeProduct = (p) => {
  if (!p || typeof p !== "object") return p;
  // Already in template shape (demo data) — leave alone.
  if (typeof p.price === "number" && Array.isArray(p.images)) return p;
  const cents = p.price_cents;
  const price = typeof cents === "number" ? cents / 100 : (typeof p.price === "number" ? p.price : 0);
  const compareCents = p.compare_at_price_cents;
  const compareAt = typeof compareCents === "number" ? compareCents / 100 : (p.compare_at_price ?? null);
  const id = p.product_id || p.id || p.slug || "";
  const slug = p.slug || id;
  let images = [];
  if (Array.isArray(p.images) && p.images.length) {
    images = p.images.map((x) => (typeof x === "string" ? x : x?.url || ""));
  } else if (p.image_url) {
    images = [p.image_url];
  } else {
    images = [""];
  }
  return {
    id,
    slug,
    name: p.name || "",
    category: p.category || (p.kind === "service" ? "services" : p.kind === "subscription" ? "subscriptions" : ""),
    kind: p.kind || "product",
    price,
    ...(compareAt ? { compare_at_price: compareAt } : {}),
    rating: typeof p.rating === "number" ? p.rating : 4.8,
    review_count: typeof p.review_count === "number" ? p.review_count : 0,
    stock: typeof p.stock === "number" ? p.stock : 99,
    featured: !!p.featured,
    images,
    description: p.description || "",
    ...(Array.isArray(p.variantAxes) ? { variantAxes: p.variantAxes } : {}),
    ...(p.recurring_interval ? { recurring_interval: p.recurring_interval } : {}),
  };
};

export const fetchProducts = async ({ slug, fallback, ...params } = {}) =>
  safe(async () => {
    if (slug) {
      // Real per-business store: hit the public site endpoint, return list.
      const { data } = await api.get(`/public/site/${slug}/products`);
      return (data?.products || []).map(normalizeProduct);
    }
    const { data } = await api.get("/products", { params });
    return Array.isArray(data) ? data.map(normalizeProduct) : data;
  }, fallback);

export const fetchProduct = async (productSlug, { slug, fallback } = {}) =>
  safe(async () => {
    if (slug) {
      // Backend lists products only — fetch list + filter client-side by slug.
      const { data } = await api.get(`/public/site/${slug}/products`);
      const list = (data?.products || []).map(normalizeProduct);
      const hit = list.find((p) => (p.slug || p.id) === productSlug);
      if (!hit) throw new Error("Product not found");
      return hit;
    }
    const { data } = await api.get(`/products/${productSlug}`);
    return normalizeProduct(data);
  }, fallback);

// Stripe-Connect checkout for a real published store (one product per session).
export const createStoreCheckout = async (slug, { product_id, customer_email, success_url, cancel_url }) => {
  const { data } = await api.post(`/public/site/${slug}/checkout`, {
    product_id, customer_email, success_url, cancel_url,
  });
  return data;
};

// Stripe-Connect multi-line cart checkout. `items` is the cart items[] from
// CartContext — each carries product_id + quantity + variant. The backend
// validates all products belong to the same business + rejects subscription
// products (Stripe disallows mixing modes in one session).
export const createCartCheckout = async (slug, { items, email, name, origin_url }) => {
  const { data } = await api.post(`/public/site/${slug}/checkout/cart`, {
    items: items.map((it) => ({
      product_id: it.product_id,
      quantity: it.quantity,
      variant: it.variant || {},
    })),
    email,
    name,
    origin_url: origin_url || window.location.origin,
  });
  return data;
};

// Polls the Stripe session status after redirect-back from Stripe.
export const fetchCheckoutSession = async (slug, sessionId) => {
  const { data } = await api.get(`/public/site/${slug}/checkout/${sessionId}`);
  return data;
};

// ────────────────────────────────────────────────────────────────────────────
// Order persistence — falls back to localStorage when the backend `/orders`
// endpoint isn't wired (template-ecommerce runs in demo / preview mode where
// the real Stripe-Connect checkout is handled by `/api/public/site/:slug/*`).
// This lets every template preset complete the full cart → checkout → success
// flow without needing a real business connected to Stripe.
// ────────────────────────────────────────────────────────────────────────────

const LOCAL_ORDERS_KEY = "iti_template_orders_v1";

const readLocalOrders = () => {
  try { return JSON.parse(localStorage.getItem(LOCAL_ORDERS_KEY) || "{}"); }
  catch { return {}; }
};

const writeLocalOrders = (obj) => {
  try { localStorage.setItem(LOCAL_ORDERS_KEY, JSON.stringify(obj)); } catch {/* ignore quota */}
};

const persistLocalOrder = (order) => {
  const all = readLocalOrders();
  all[order.order_number] = order;
  writeLocalOrders(all);
};

const generateOrderNumber = () => {
  const ts = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `ITI-${ts}-${rand}`;
};

const shouldFallbackToLocal = (err) => {
  // Fall back to localStorage ONLY for 404 / network errors. Real 5xx errors
  // bubble up to the caller so a future real-checkout failure isn't silently
  // hidden behind a fake-but-successful local order.
  const status = err?.response?.status;
  if (status === 404) return true;
  if (status === undefined) return true; // network / CORS / no response
  return false;
};

export const createOrder = async (payload) => {
  try {
    const { data } = await api.post("/orders", payload);
    return data;
  } catch (err) {
    if (!shouldFallbackToLocal(err)) throw err;
    const order = {
      order_number: generateOrderNumber(),
      created_at: new Date().toISOString(),
      ...payload,
    };
    persistLocalOrder(order);
    return order;
  }
};

export const fetchOrder = async (orderNumber, { fallback } = {}) => {
  try {
    const { data } = await api.get(`/orders/${orderNumber}`);
    return data;
  } catch (err) {
    if (!shouldFallbackToLocal(err)) throw err;
    const local = readLocalOrders()[orderNumber];
    if (local) return local;
    if (fallback !== undefined) return fallback;
    throw new Error("Order not found");
  }
};

export const subscribeNewsletter = async (email) => {
  const { data } = await api.post("/newsletter", { email });
  return data;
};
